import { Topbar } from "../components/Topbar";
import { KpiCard } from "../components/KpiCard";
import { Panel } from "../components/Panel";
import { DataTable } from "../components/DataTable";
import { LoadingState, ErrorState } from "../components/States";
import { useDashboard } from "../hooks/useDashboard";
import { formatCurrency, formatMonthLabel, formatNumber } from "../lib/dates";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#002FA7", "#3B82F6", "#94A3B8", "#10B981", "#F59E0B", "#EF4444"];

export default function Purchases() {
  const { data, loading, error } = useDashboard("purchases");

  return (
    <>
      <Topbar title="Compras" subtitle="Órdenes de compra y proveedores" />
      <div className="p-6 space-y-6" data-testid="purchases-page">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard testId="kpi-po-orders" label="Órdenes" value={formatNumber(data.kpis.total_orders, { maximumFractionDigits: 0 })} />
              <KpiCard testId="kpi-po-amount" label="Importe total" value={formatCurrency(data.kpis.total_amount)} />
              <KpiCard testId="kpi-po-avg" label="Ticket medio" value={formatCurrency(data.kpis.avg_order)} />
              <KpiCard testId="kpi-po-suppliers" label="Top proveedores" value={formatNumber(data.kpis.unique_suppliers, { maximumFractionDigits: 0 })} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Panel title="Evolución mensual" className="lg:col-span-2" testId="chart-po-trend">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fontSize: 11, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => formatCurrency(v)} />
                      <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={formatMonthLabel} />
                      <Bar dataKey="total" name="Compras" fill="#002FA7" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              <Panel title="Por estado" testId="chart-po-status">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.status_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45}>
                        {data.status_distribution.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Panel title="Top 10 proveedores" testId="table-top-suppliers">
                <DataTable
                  testId="suppliers-table"
                  filename="top_proveedores.csv"
                  rows={data.top_suppliers}
                  columns={[
                    { key: "supplier", label: "Proveedor", accessor: "supplier" },
                    { key: "orders", label: "Órdenes", accessor: "orders" },
                    {
                      key: "amount", label: "Importe", accessor: "amount",
                      render: (r) => <span className="tabular-nums font-medium">{formatCurrency(r.amount)}</span>,
                    },
                  ]}
                />
              </Panel>

              <Panel title="Órdenes recientes" testId="table-po-recent">
                <DataTable
                  testId="po-recent-table"
                  filename="ordenes_compra.csv"
                  rows={data.recent_orders}
                  columns={[
                    { key: "name", label: "Referencia", accessor: "name" },
                    { key: "supplier", label: "Proveedor", accessor: "supplier" },
                    { key: "date", label: "Fecha", accessor: "date" },
                    {
                      key: "amount", label: "Importe", accessor: "amount",
                      render: (r) => <span className="tabular-nums font-medium">{formatCurrency(r.amount)}</span>,
                    },
                    { key: "state", label: "Estado", accessor: "state" },
                  ]}
                />
              </Panel>
            </div>
          </>
        )}
      </div>
    </>
  );
}
